import React, { useState, useEffect } from 'react';
import { devLog } from '../lib/log';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from './PasswordInput';
import { markPendingConsent } from '../lib/legal';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [consent, setConsent] = useState(false); // PB-002: consenso per login Google
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithGoogle, resetPassword, user, profile } = useAuth();

  // PB-002 — Login con Google. Il consenso va spuntato PRIMA (lo stato del form
  // si perde nel redirect): lo persistiamo in localStorage e lo riscriviamo sul
  // profilo al ritorno (AuthContext.loadProfile), con gate di backstop.
  const handleGoogle = async () => {
    if (!consent) { setError('Per continuare accetta Privacy e Termini.'); return; }
    setError(''); setInfo('');
    markPendingConsent();
    try {
      const { error: oauthError } = await signInWithGoogle();
      if (oauthError) setError(oauthError.message || 'Errore con l\'accesso Google.');
    } catch (e) {
      setError('Errore con l\'accesso Google.');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email) { setError('Inserisci la tua email'); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      setInfo('Se esiste un account con questa email, ti abbiamo inviato un link per reimpostare la password.');
    } catch (err) {
      // Messaggio neutro per non rivelare se l'email esiste
      setInfo('Se esiste un account con questa email, ti abbiamo inviato un link per reimpostare la password.');
    } finally {
      setLoading(false);
    }
  };

  // Sessione scaduta altrove nell'app → messaggio informativo, non allarmistico.
  // Dipende da location.state: il redirect può arrivare quando Login è GIÀ
  // montato (es. race col redirect di ProtectedRoute), non solo al mount.
  useEffect(() => {
    if (location.state?.sessionExpired) {
      setInfo('La sessione è scaduta. Accedi di nuovo per continuare.');
      // Non ripresentare il messaggio su back/refresh
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.state]);

  // Gestisci errori/parametri nell'URL hash al mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const errorParam = params.get('error');
      const errorCode = params.get('error_code');
      const errorDescription = params.get('error_description');

      if (errorParam) {
        let errorMessage = 'Errore di autenticazione';

        if (errorCode === 'otp_expired') {
          errorMessage = 'Il link di conferma è scaduto. Prova a registrarti di nuovo o a fare login.';
        } else if (errorDescription) {
          errorMessage = decodeURIComponent(errorDescription);
        } else {
          errorMessage = `Errore: ${errorParam}`;
        }

        setError(errorMessage);

        // Pulisci l'URL rimuovendo l'hash
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  // Redirect se già loggato
  useEffect(() => {
    /*#__PURE__*/ devLog('🔵 [LOGIN] Redirect useEffect triggered');
    /*#__PURE__*/ devLog('🔵 [LOGIN] user:', user?.id);
    /*#__PURE__*/ devLog('🔵 [LOGIN] profile:', profile);

    if (user && profile) {
      // Profilo unico: tutti entrano nell'app atleta; le funzioni coach sono
      // accessibili dalla voce "Coach" quando la Modalità Coach è attiva.
      const redirectPath = profile.role === 'admin' ? '/admin' : '/athlete';
      /*#__PURE__*/ devLog('✅ [LOGIN] Redirecting to:', redirectPath);
      navigate(redirectPath, { replace: true });
    } else if (user && !profile) {
      console.warn('⚠️ [LOGIN] User exists but profile is null - waiting for profile to load...');
    }
  }, [user, profile, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    /*#__PURE__*/ devLog('🔵 [LOGIN] Starting login flow...');
    /*#__PURE__*/ devLog('🔵 [LOGIN] Email:', email);

    if (!email || !password) {
      setError('Inserisci email e password');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      /*#__PURE__*/ devLog('🔵 [LOGIN] Calling signIn...');
      const result = await signIn(email, password);

      /*#__PURE__*/ devLog('🔵 [LOGIN] SignIn result:', result);
      /*#__PURE__*/ devLog('🔵 [LOGIN] Has user?', !!result?.user);
      /*#__PURE__*/ devLog('🔵 [LOGIN] Has session?', !!result?.session);
      /*#__PURE__*/ devLog('🔵 [LOGIN] User ID:', result?.user?.id);
      /*#__PURE__*/ devLog('🔵 [LOGIN] User email confirmed?', result?.user?.email_confirmed_at);

      // Controlla se serve conferma email
      if (result?.user && !result?.session) {
        /*#__PURE__*/ devLog('⚠️ [LOGIN] No session - email confirmation required');
        setInfo('Controlla la tua email per confermare l\'account prima di accedere.');
        setLoading(false);
        return;
      }

      /*#__PURE__*/ devLog('✅ [LOGIN] Login successful, waiting for redirect...');
      // Il redirect sarà gestito dall'useEffect sopra quando user e profile saranno disponibili
    } catch (error) {
      console.error('❌ [LOGIN] Login error:', error);
      console.error('❌ [LOGIN] Error message:', error.message);
      console.error('❌ [LOGIN] Error details:', JSON.stringify(error, null, 2));

      // Mostra l'errore REALE di Supabase
      let errorMessage = error.message || 'Errore durante il login';

      // Solo traduci i messaggi più comuni, altrimenti mostra l'errore originale
      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Email o password non corretti';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Email non confermata. Controlla la tua casella di posta.';
      }

      setError(errorMessage);
    } finally {
      /*#__PURE__*/ devLog('🔵 [LOGIN] Setting loading to false');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Uptra</h1>
        {mode === 'login' ? (
          <>
            <p className="login-subtitle">Accedi al tuo account</p>
            <form className="login-form" onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email" id="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="inserisci la tua email" required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <PasswordInput
                  id="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="inserisci la tua password" required
                  autoComplete="current-password"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              {info && <div className="info-message">{info}</div>}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Caricamento...' : 'Accedi'}
              </button>
            </form>

            {/* PB-002 — Accesso con Google */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '18px 0 12px' }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>oppure</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
            </div>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#374151', margin: '0 0 10px', lineHeight: 1.4 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: '2px', flexShrink: 0 }} />
              <span>
                Dichiaro di aver letto l'<Link to="/privacy" target="_blank" rel="noopener noreferrer">Informativa Privacy</Link> e
                accetto i <Link to="/termini" target="_blank" rel="noopener noreferrer">Termini di utilizzo</Link>.
              </span>
            </label>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={!consent}
              style={{ width: '100%', padding: '12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: consent ? 'pointer' : 'not-allowed', opacity: consent ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
              Continua con Google
            </button>

            <div className="login-footer">
              <p>
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
                >
                  Password dimenticata?
                </button>
              </p>
              <p>Sei nuovo? <Link to="/register">Registrati</Link></p>
            </div>
          </>
        ) : (
          <>
            <p className="login-subtitle">Recupera la password</p>
            <form className="login-form" onSubmit={handleForgot}>
              <div className="form-group">
                <label htmlFor="email-forgot">Email</label>
                <input
                  type="email" id="email-forgot" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="la tua email" required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              {info && <div className="info-message">{info}</div>}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Invio…' : 'Invia link di reset'}
              </button>
            </form>
            <div className="login-footer">
              <p>
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); setInfo(''); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
                >
                  ← Torna all'accesso
                </button>
              </p>
            </div>
          </>
        )}

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '16px' }}>
          <Link to="/privacy" style={{ color: '#9ca3af' }}>Privacy</Link>
          {' · '}
          <Link to="/termini" style={{ color: '#9ca3af' }}>Termini</Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
